import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { pick, debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Form, Spinner } from 'components';
import { ONBOARDING } from 'constants/routes';
import { currencies } from 'constants/currenciesList';
import { useFormikContext } from 'formik';
import { ErrorFeedback } from 'components/Form/styles';
import { useAuthStore } from 'stores/authStore';
import {
  useCreateRegion,
  useCheckRegionAvailability,
  useCheckSubdomainAvailability,
} from 'api/regions/hooks';
import { useUpdateBrand } from 'api/brand/hooks';
import validationSchemas from './schemas';

const RegionAvailabilityCheck = ({ debouncedCheck, isAvailable, validateFieldName }) => {
  const { values, validateField } = useFormikContext();

  useEffect(() => {
    if (values?.name?.length) {
      debouncedCheck(values.name);
    }

    return () => {
      debouncedCheck.cancel();
    };
  }, [values.name, debouncedCheck]);

  useEffect(() => {
    validateField(validateFieldName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAvailable, validateField]);

  return null;
};

const DomainAvailabilityCheck = ({ debouncedCheck, isAvailable, validateFieldName }) => {
  const { values, validateField } = useFormikContext();

  useEffect(() => {
    if (values?.subdomain?.length) {
      debouncedCheck(values.subdomain);
    }

    return () => {
      debouncedCheck.cancel();
    };
  }, [values.subdomain, debouncedCheck]);

  useEffect(() => {
    validateField(validateFieldName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAvailable, validateField]);

  return null;
};

const OnboardingRegionForm = ({ initialValues, onNext }) => {
  const [regionNameToCheck, setRegionNameToCheck] = useState(null);
  const [subdomainToCheck, setSubdomainToCheck] = useState(null);

  const { t } = useTranslation(['pages', 'app']);

  const location = useLocation();
  const brand = useAuthStore((state) => state.user?.admin_details?.brand);

  const { mutateAsync: createRegion, isPending: isCreateRegionPending } = useCreateRegion();
  const { mutateAsync: updateBrand } = useUpdateBrand();
  const { data: regionAvailabilityData, isLoading: isRegionLoading } =
    useCheckRegionAvailability(regionNameToCheck);

  const { data: subdomainAvailabilityData, isLoading: isDomainLoading } =
    useCheckSubdomainAvailability(subdomainToCheck);

  const isRegionAvailable = regionAvailabilityData?.available ?? false;
  const regionAvailabilityMessage = regionAvailabilityData?.message;
  const isDomainAvailable = subdomainAvailabilityData?.available ?? false;
  const domainAvailabilityMessage = subdomainAvailabilityData?.message;

  const domainTaken = 'fe-x-circle text-danger';
  const domainAvailable = 'fe-check-circle text-success';

  const debouncedCheckRegion = useMemo(
    () =>
      debounce(
        (name) => {
          if (name && name.length > 0) {
            setRegionNameToCheck(name);
          }
        },
        500,
        { leading: false, trailing: true },
      ),
    [],
  );

  const debouncedCheckSubdomain = useMemo(
    () =>
      debounce(
        (subdomain) => {
          if (subdomain && subdomain.length > 0) {
            setSubdomainToCheck(subdomain);
          }
        },
        500,
        { leading: false, trailing: true },
      ),
    [],
  );

  return (
    <Form
      initialValues={{
        ...pick(initialValues, ['uid', 'name', 'full_domain', 'base_currency']),
        subdomain: initialValues?.full_domain?.split('.')?.[0],
      }}
      onSubmit={async ({ name, subdomain, base_currency }) => {
        try {
          await createRegion({
            name,
            subdomain: subdomain?.toLowerCase(),
            base_currency,
            brand_uid: brand?.uid,
            is_onboarding: location.pathname.includes(ONBOARDING),
          });

          onNext();

          await updateBrand({
            uid: brand?.uid,
            is_onboarding_process_finished: true,
          });
        } catch (error) {
          console.error('Error submitting region form:', error);
        }
      }}
      validationSchema={validationSchemas.region}
      renderDefaultButtons={false}
      pristine
    >
      {({ values, initialValues: formikInitialValues, errors, touched }) => {
        const hasRegionChanged = values?.name !== formikInitialValues?.name;
        const hasDomainChanged = values?.subdomain !== formikInitialValues?.subdomain;

        return (
          <>
            <Form.Group label={t('pages:regions.name')}>
              <Form.Label tooltipText={t('regionRegistration.nameTooltip')}>
                {t('regionRegistration.name')}
              </Form.Label>
              <Form.Input
                type="text"
                name="name"
                validate={() =>
                  !hasRegionChanged || isRegionAvailable ? undefined : regionAvailabilityMessage
                }
                showErrorFeedback={false}
                prepend={
                  isRegionLoading ? (
                    <Spinner style={{ width: '17px', height: '17px' }} />
                  ) : (!hasRegionChanged || isRegionAvailable) && !errors?.name ? (
                    <i className={`fe ${domainAvailable}`} />
                  ) : (
                    <i className={`fe ${domainTaken}`} />
                  )
                }
              />
              <RegionAvailabilityCheck
                debouncedCheck={debouncedCheckRegion}
                isAvailable={isRegionAvailable}
                validateFieldName="name"
              />
              {((touched.name && errors.name) || errors.name === regionAvailabilityMessage) && (
                <ErrorFeedback>{errors.name}</ErrorFeedback>
              )}
            </Form.Group>
            <Form.Group label={t('pages:regions.subdomain')}>
              <Form.Label tooltipText={t('regionRegistration.subdomainTooltip')}>
                {t('regionRegistration.subdomain')}
              </Form.Label>
              <Form.Input
                type="text"
                name="subdomain"
                append=".ramblas.io"
                validate={() =>
                  !hasDomainChanged || isDomainAvailable ? undefined : domainAvailabilityMessage
                }
                showErrorFeedback={false}
                prepend={
                  isDomainLoading ? (
                    <Spinner style={{ width: '17px', height: '17px' }} />
                  ) : (!hasDomainChanged || isDomainAvailable) && !errors?.subdomain ? (
                    <i className={`fe ${domainAvailable}`} />
                  ) : (
                    <i className={`fe ${domainTaken}`} />
                  )
                }
              />
              <DomainAvailabilityCheck
                debouncedCheck={debouncedCheckSubdomain}
                isAvailable={isDomainAvailable}
                validateFieldName="subdomain"
              />
              {((touched.subdomain && errors.subdomain) ||
                errors.subdomain === domainAvailabilityMessage) && (
                <ErrorFeedback>{errors.subdomain}</ErrorFeedback>
              )}
            </Form.Group>
            <Form.Group label={t('pages:regions.currency')}>
              <Form.Label>{t('regionRegistration.currency')}</Form.Label>
              <Form.MultiSelect
                options={currencies.map((currency) => ({
                  value: currency.code,
                  label: currency.description,
                }))}
                name="base_currency"
                isMulti={false}
              />
            </Form.Group>
            <Form.Submit
              className="mt-3 text-uppercase"
              size="lg"
              variant="primary"
              pristine
              ignoreClickedState
              isLoading={isCreateRegionPending}
            >
              {t('onboarding.nextStep')}
            </Form.Submit>
          </>
        );
      }}
    </Form>
  );
};

OnboardingRegionForm.propTypes = {
  initialValues: PropTypes.object,
  onNext: PropTypes.func.isRequired,
};

export default OnboardingRegionForm;
